import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionStatusChip, BlueUnderlineButton } from './common';
import { AddPetDetailsModal } from './AddPetDetailsModal';
import { PetReplaceModal } from './PetReplaceModal';

import { ReactComponent as DogIcon } from '../../assets/welcome/dog-avatar.svg';
import { ReactComponent as CatIcon } from '../../assets/welcome/cat-avatar.svg';
import { PET_DEMOS_SPECIES, PET_INFO_CARD_STATUS } from '../../constants';

import styles from './PetDemographicsPetCard.module.css';

const PLACEHOLDER_VALUE = '--';

const FieldRowVertical = ({ title = '', value = '', isFilled = false }) => (
  <div className={styles.columnField}>
    <h6 className={styles.rowLabel}>{title}</h6>

    <span className={isFilled ? styles.rowValue : styles.rowPlaceholder}>
      {isFilled ? value : PLACEHOLDER_VALUE}
    </span>
  </div>
);

const FieldContainer = ({ title = '', value = '', isFilled = false }) => (
  <div className={styles.fieldContainer}>
    <h6 className={styles.rowLabel}>{title}</h6>

    <span className={isFilled ? styles.rowValue : styles.rowPlaceholder}>
      {isFilled ? value : PLACEHOLDER_VALUE}
    </span>
  </div>
);

const PetDemographicsPetCard = ({
  age = '',
  breed = '',
  breedId,
  coverageEffectiveDate = '',
  currentPet = null,
  dateOfBirth = '',
  isCompleted = false,
  isReadOnly = false,
  isReplaced = false,
  isRolloverPet = false,
  onCancelReplacement = () => { },
  onConfirmReplacement = () => { },
  onReplaceSubmit = () => { },
  onUndoReplace = () => { },
  originalPetName = '',
  petName = '',
  petNumber = 0,
  specie = PET_DEMOS_SPECIES.dog,
  updateDemographics = () => { },
  weight = '',
}) => {
  const { t } = useTranslation('petDemos');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isReplaceModalVisible, setIsReplaceModalVisible] = useState(false);
  const [isReplacementFlow, setIsReplacementFlow] = useState(false);
  const [isUndoModalVisible, setIsUndoModalVisible] = useState(false);

  const IconComponent = specie === PET_DEMOS_SPECIES.dog ? DogIcon : CatIcon;
  const speciesLabel = specie === PET_DEMOS_SPECIES.dog ? t('dog') : t('cat');

  const getStatusInfo = () => {
    let statusKey = 'pending';

    if (isCompleted) {
      statusKey = 'completed';
    }

    if (isReplaced) {
      statusKey = 'replaced';
    }

    return {
      statusKey,
      statusLabel: t(`completeInformation.status.${statusKey}`),
    };
  };

  const cardStatus = PET_INFO_CARD_STATUS[getStatusInfo().statusKey];

  const getActionLabelTitle = () => {
    if (isRolloverPet && !isReplaced) {
      return t('completeInformation.replace');
    }

    if (isRolloverPet && isReplaced) {
      return t('completeInformation.editDetails');
    }

    if (isCompleted) {
      return t('completeInformation.editDetails');
    }

    return t('completeInformation.addDetails');
  };

  const actionLabelTitle = getActionLabelTitle();

  const handleOnActionClick = () => {
    if (isRolloverPet && !isReplaced) {
      setIsReplaceModalVisible(true);
      return;
    }

    if (isRolloverPet && isReplaced) {
      setIsReplacementFlow(false);
      setIsModalVisible(true);
      return;
    }

    setIsReplacementFlow(false);
    setIsModalVisible(true);
  };

  const handleOnCloseAddDetailsModal = () => {
    if (isReplacementFlow) {
      onCancelReplacement(petNumber);
    }
    setIsModalVisible(false);
    setIsReplacementFlow(false);
  };

  const handleConfirmReplacement = () => {
    onConfirmReplacement(currentPet, petNumber);
    setIsReplacementFlow(true);
    setIsReplaceModalVisible(false);
    setIsModalVisible(true);
  };

  const handleOnUndoClick = () => {
    setIsUndoModalVisible(true);
  };

  const handleOnUndoReplacement = () => {
    onUndoReplace(petNumber);
    setIsUndoModalVisible(false);
  };

  const handleOnCloseReplaceModal = () => {
    setIsReplaceModalVisible(false);
  };

  const handleOnCloseUndoModal = () => {
    setIsUndoModalVisible(false);
  };

  const handleOnAddDetailsModalSubmit = (updatedDetails) => {
    if (isReplacementFlow) {
      onReplaceSubmit(updatedDetails, petNumber);
    } else {
      updateDemographics(updatedDetails, petNumber);
    }
    setIsModalVisible(false);
    setIsReplacementFlow(false);
  };

  const addModalInitialProps = isReplacementFlow
    ? {
      age: '',
      breed: '',
      coverageEffectiveDate,
      dateOfBirth: '',
      petName: '',
      specie,
      weight: '',
    }
    : {
      age,
      breed,
      breedId,
      coverageEffectiveDate,
      dateOfBirth,
      petName,
      specie,
      weight,
    };

  const hasValue = (value) => String(value).trim() !== '' && value !== 0;

  return (
    <div
      className={`${styles.petCard} ${isCompleted && styles.petCardCompleted}`}
    >
      <div className={styles.topCardContainer}>
        <IconComponent className={styles.petAvatar} data-specie={specie} />

        <div className={styles.statusChipContainerMobile}>
          <ActionStatusChip
            status={cardStatus}
            statusLabel={getStatusInfo().statusLabel}
          >
            {isReplaced && (
              <BlueUnderlineButton
                className={styles.undoButton}
                onClick={handleOnUndoClick}
                title={t('completeInformation.undo')}
              />
            )}
          </ActionStatusChip>
        </div>
      </div>

      <div className={styles.column}>
        <FieldContainer
          isFilled={hasValue(petName)}
          title={t('petName')}
          value={petName}
        />

        <FieldContainer
          isFilled={hasValue(breed)}
          title={t('breed')}
          value={breed}
        />
      </div>

      <div className={styles.row}>
        <FieldRowVertical
          isFilled={hasValue(speciesLabel)}
          title={t('specie')}
          value={speciesLabel}
        />

        <FieldRowVertical
          isFilled={hasValue(dateOfBirth)}
          title={t('age')}
          value={hasValue(dateOfBirth)
            ? t('completeInformation.ageValue', { age })
            : '--'}
        />

        <FieldRowVertical
          isFilled={hasValue(weight)}
          title={t('weight')}
          value={weight
            ? t('completeInformation.weightValue', { weight })
            : '--'}
        />
      </div>

      <div className={styles.buttonContainer}>
        <div className={styles.statusChipContainer}>
          <ActionStatusChip
            status={cardStatus}
            statusLabel={getStatusInfo().statusLabel}
          >
            {isReplaced && (
              <BlueUnderlineButton
                className={styles.undoButton}
                onClick={handleOnUndoClick}
                title={t('completeInformation.undo')}
              />
            )}
          </ActionStatusChip>
        </div>

        {!isReadOnly && (
          <BlueUnderlineButton
            onClick={handleOnActionClick}
            title={actionLabelTitle}
          />
        )}
      </div>

      {isModalVisible && (
        <AddPetDetailsModal
          {...addModalInitialProps}
          isOpen={isModalVisible}
          onClose={handleOnCloseAddDetailsModal}
          onSubmit={handleOnAddDetailsModalSubmit}
        />
      )}

      {isReplaceModalVisible && (
        <PetReplaceModal
          endDate={coverageEffectiveDate}
          isOpen={isReplaceModalVisible}
          onClose={handleOnCloseReplaceModal}
          onConfirmReplacement={handleConfirmReplacement}
          onUndoReplacement={handleOnUndoReplacement}
          originalPet={petName}
          petReplace={petName}
          undoReplace={false}
        />
      )}

      {isUndoModalVisible && (
        <PetReplaceModal
          isOpen={isUndoModalVisible}
          onClose={handleOnCloseUndoModal}
          onUndoReplacement={handleOnUndoReplacement}
          originalPet={originalPetName}
          petReplace={petName}
          undoReplace
        />
      )}
    </div>
  );
};

export { PetDemographicsPetCard };
